/*
 * stories-flutuante.js — flutuar, arrastar e dispensar a bolinha de vídeo.
 *
 * É uma CAMADA POR FORA do player. Quem abre o vídeo, passa de um story para o outro e
 * desenha as barrinhas continua sendo `product-stories-videos.js`, que veio minificado
 * e sem fonte — mexer nele seria editar código gerado. Aqui só se cuida de onde a
 * bolinha fica e de como ela sai da frente; os dois convivem no mesmo elemento porque
 * o player procura as classes internas e este arquivo, as de fora.
 *
 * O CUIDADO QUE FAZ ISSO FUNCIONAR: o player abre o vídeo no `click` do item. Se o
 * arrasto não fosse distinguido do clique, todo gesto para dispensar terminaria com o
 * vídeo aberto em tela cheia — exatamente o contrário do que a pessoa pediu. Por isso
 * o movimento só vira arrasto depois de 6px, e nesse caso o clique seguinte é engolido
 * na fase de captura, antes de chegar ao player.
 */
(() => {
  var CHAVE = 'cbv-stories-dispensado';
  var LIMIAR_ARRASTO = 6; // px para separar clique de arrasto
  var LIMIAR_SAIDA = 60; // px para a esquerda que confirmam "tire isso daqui"

  function iniciar(raiz) {
    if (!raiz || raiz.dataset.flutuanteLigado === '1') return;
    raiz.dataset.flutuanteLigado = '1';

    var fechar = raiz.querySelector('[data-js-stories-fechar]');
    var x0 = 0;
    var dx = 0;
    var arrastando = false;
    var houveArrasto = false;

    function dispensar() {
      raiz.classList.add('stories-flutuante--saindo');
      try {
        sessionStorage.setItem(CHAVE, '1');
      } catch (e) {
        /* navegação privada pode recusar; a bolinha some nesta página do mesmo jeito */
      }
      setTimeout(function () {
        raiz.remove();
      }, 300);
    }

    if (fechar) {
      fechar.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        dispensar();
      });
    }

    raiz.addEventListener('pointerdown', function (e) {
      if (e.target.closest('[data-js-stories-fechar]')) return;
      // O modal do player é filho desta raiz: com ele aberto, arrastar não faz sentido.
      if (raiz.querySelector('.product-stories-modal.active')) return;
      arrastando = true;
      houveArrasto = false;
      x0 = e.clientX;
      dx = 0;
    });

    raiz.addEventListener('pointermove', function (e) {
      if (!arrastando) return;
      dx = e.clientX - x0;
      if (!houveArrasto && Math.abs(dx) < LIMIAR_ARRASTO) return;
      if (!houveArrasto) {
        houveArrasto = true;
        raiz.classList.add('stories-flutuante--arrastando');
        // Só captura o ponteiro depois de virar arrasto — antes disso o clique
        // ainda pertence ao player.
        if (raiz.setPointerCapture) {
          try { raiz.setPointerCapture(e.pointerId); } catch (err) { /* ponteiro já solto */ }
        }
      }
      // Só para a esquerda: puxar para a direita empurraria a bolinha para cima do
      // conteúdo, e o gesto de dispensar é um só.
      raiz.style.transform = 'translateX(' + Math.min(0, dx) + 'px)';
    });

    function soltar() {
      if (!arrastando) return;
      arrastando = false;
      raiz.classList.remove('stories-flutuante--arrastando');
      raiz.style.transform = '';
      if (houveArrasto && dx <= -LIMIAR_SAIDA) dispensar();
      /*
       * Zera DEPOIS do `click`, que o navegador dispara logo em seguida ao `pointerup`
       * — e antes de qualquer tarefa agendada, por isso o setTimeout de 0.
       *
       * Sem isto, um arrasto que não termina em clique (soltar fora da bolinha, um
       * gesto que não chegou ao limiar de saída) deixava a marca ligada, e o PRÓXIMO
       * clique era engolido como se ainda fosse aquele arrasto. No teste de 07/08/2026
       * a bolinha abria o vídeo na primeira vez e ficava muda daí em diante.
       */
      setTimeout(function () {
        houveArrasto = false;
      }, 0);
    }

    raiz.addEventListener('pointerup', soltar);
    raiz.addEventListener('pointercancel', soltar);

    // Captura: chega aqui ANTES do listener do player, que vive no item interno.
    raiz.addEventListener(
      'click',
      function (e) {
        if (!houveArrasto) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );
  }

  /*
   * O player foi escrito para tela cheia e faz duas coisas ao abrir que brigam com um
   * vídeo tocando no canto:
   *
   *   document.body.style.overflow = 'hidden'          trava a rolagem da página
   *   .product-infos.style.zIndex  = '9999999'         ergue o bloco de compra
   *
   * Ambas faziam sentido quando o vídeo tomava a tela: não havia o que rolar atrás, e
   * o modal era filho de `.product-infos`, então subir o pai subia o modal junto. Com
   * a janela no canto, a primeira impede a pessoa de continuar navegando enquanto
   * assiste, e a segunda joga o botão Comprar por cima do vídeo.
   *
   * O arquivo do player é minificado e sem fonte, então as duas são desfeitas aqui,
   * assim que a classe `active` aparece. Observar a classe (e não o clique) cobre
   * todos os caminhos: clique, tecla Escape, avanço automático no fim do último vídeo.
   */
  function acompanharPlayer(raiz) {
    var modal = raiz.querySelector('.product-stories-modal');
    if (!modal) return;
    var infos = document.querySelector('.product-infos');
    var aplicar = function () {
      var tocando = modal.classList.contains('active');
      raiz.classList.toggle('stories-flutuante--tocando', tocando);
      if (tocando) {
        document.body.style.overflow = '';
        if (infos) infos.style.zIndex = '';
      }
    };
    aplicar();
    new MutationObserver(aplicar).observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  /*
   * CADA STORY TEM O SEU BOTÃO, e só um aparece por vez.
   *
   * Os links são renderizados todos de uma vez (o Liquid roda uma vez só) e o player
   * troca de story marcando com `active` o <video> da vez. Aqui a gente segue essa
   * marca em vez de manter uma contagem própria — assim vale para todos os caminhos de
   * navegação, inclusive os que não passam por clique: teclado, swipe e o avanço
   * automático quando um vídeo termina.
   *
   * O clique no botão para de subir de propósito: o player escuta o clique no modal
   * inteiro para decidir voltar, avançar ou pausar conforme a posição na tela. Sem o
   * `stopPropagation`, tocar no link também pausaria ou pularia o story no caminho.
   */
  function acompanharLinks(raiz) {
    var links = raiz.querySelectorAll('[data-js-stories-link]');
    if (!links.length) return;
    var conteudo = raiz.querySelector('.product-stories-modal__content');
    if (!conteudo) return;

    var sincronizar = function () {
      var ativo = conteudo.querySelector('.product-stories-modal__video.active');
      var idx = ativo ? ativo.getAttribute('data-story-index') : null;
      links.forEach(function (a) {
        a.hidden = a.getAttribute('data-story-index') !== idx;
      });
    };

    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    });

    sincronizar();
    new MutationObserver(sincronizar).observe(conteudo, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  /*
   * A barra de comprar cobre o rodapé e vai e volta com a rolagem (classe `hidden`).
   * Em vez de escutar o scroll — que dispara dezenas de vezes por segundo — observa-se
   * a mudança de classe dela, que acontece só na virada.
   *
   * Ela está DESATIVADA hoje (o render está comentado em product-infos.liquid), então
   * na prática este trecho sai na primeira linha. Fica como seguro: religar a barra
   * não pode fazer a bolinha aparecer por cima do botão de comprar.
   */
  function acompanharBarra(raiz) {
    var barra = document.querySelector('.sticky-buy-bar');
    if (!barra) return;
    var ajustar = function () {
      raiz.classList.toggle('stories-flutuante--acima-da-barra', !barra.classList.contains('hidden'));
    };
    ajustar();
    new MutationObserver(ajustar).observe(barra, { attributes: true, attributeFilter: ['class'] });
  }

  function ligarTodas() {
    document.querySelectorAll('[data-js-stories-flutuante]').forEach(function (raiz) {
      iniciar(raiz);
      acompanharPlayer(raiz);
      acompanharLinks(raiz);
      acompanharBarra(raiz);
    });
  }

  document.addEventListener('DOMContentLoaded', ligarTodas);
  document.addEventListener('shopify:section:load', ligarTodas); // personalizador
})();
